import scrollToElement from "./scrollto";
import SearchResultKeys from "./search_result_keys";
import EventBus from "../events/EventBus";
import { StatusClassNames } from "../types/StatusClassNames";
import { TypingPlaceholder } from "./typing_placeholder";
import { extractData } from "../utils";
const URL = '/api/search';
const DEFAULT_CONFIG = { placeholder: [], enabled: false };
const configElement = document.getElementById('animation-search-config');
const config = extractData(configElement) ?? DEFAULT_CONFIG;
export class SearchBar {
    constructor(searchBar, searchResults, notFoundBlock, brandsBlock, productsBlock, icon) {
        this.searchBar = searchBar;
        this.searchResults = searchResults;
        this.notFoundBlock = notFoundBlock;
        this.brandsBlock = brandsBlock;
        this.productsBlock = productsBlock;
        this.icon = icon;
        this.shadowCache = document.createElement('div');
        this.lastValue = '';
        this.cache = {};
        this.suggestionItems = {};
        this.notFound = '';
        this.timeoutId = 0;
        if (!searchResults) {
            return;
        }
        this.brandsList = brandsBlock.querySelector('.search-result__list');
        this.productsList = productsBlock.querySelector('.search-result__list');
        this.lastValue = searchBar.value.trim();
        this.keyHandler = new SearchResultKeys(searchBar, searchResults, '.search-result__item', this.onselect.bind(this));
        this.bindEvents();
        SearchBar.setAppHeight();
        this.initTypingPlaceholder();
    }
    bindEvents() {
        const form = this.searchBar.closest('form');
        if (form) {
            form.addEventListener('submit', this.onsubmit.bind(this));
        }
        this.searchBar.addEventListener('keyup', this.onkeyup.bind(this));
        this.searchBar.addEventListener('input', this.onkeyup.bind(this));
        this.searchBar.addEventListener('blur', this.onblur.bind(this));
        this.searchBar.addEventListener('focus', this.onfocus.bind(this));
        this.searchBar.addEventListener('click', this.onfocus.bind(this));
        this.searchResults.addEventListener('focus', this.show.bind(this));
        this.searchResults.addEventListener('click', this.show.bind(this));
        this.searchBar.parentElement.querySelector('.top-search__clear').addEventListener('click', this.onclear.bind(this));
        window.addEventListener('resize', SearchBar.setAppHeight);
        document.addEventListener('click', (e) => {
            if (!this.searchBar.contains(e.target) &&
                !this.searchResults.contains(e.target)) {
                this.hide();
            }
        });
    }
    static setAppHeight() {
        document.documentElement.style.setProperty('--app-height', `${window.innerHeight}px`);
    }
    initTypingPlaceholder() {
        if (!this.searchBar) {
            return;
        }
        if (config.enabled) {
            this.typingPlaceholder = new TypingPlaceholder({
                element: this.searchBar,
                phrases: config.placeholder,
                typingDelayMs: 200, // задержка между буквами
                phrasePauseMs: 2000, // пауза между фразами
                eraseDelayMs: 80,
                shuffle: true,
            });
            this.typingPlaceholder.start();
        }
    }
    hide() {
        this.searchResults.classList.add(StatusClassNames.hidden);
    }
    show() {
        clearTimeout(this.timeoutId);
        this.searchResults.classList.remove(StatusClassNames.hidden);
    }
    toggleBlocks(brands = false, products = false) {
        if (brands) {
            this.brandsBlock.classList.remove(StatusClassNames.hidden);
        }
        else {
            this.brandsBlock.classList.add(StatusClassNames.hidden);
        }
        if (products) {
            this.productsBlock.classList.remove(StatusClassNames.hidden);
        }
        else {
            this.productsBlock.classList.add(StatusClassNames.hidden);
        }
        if (!brands && !products) {
            this.notFoundBlock.classList.remove(StatusClassNames.hidden);
        }
        else {
            this.notFoundBlock.classList.add(StatusClassNames.hidden);
        }
        if (this.searchBar.matches(':focus')) {
            this.show();
        }
    }
    onclear(event) {
        this.searchBar.value = '';
        this.searchBar.focus();
        event.preventDefault();
    }
    onfocus() {
        if (this.typingPlaceholder) {
            this.typingPlaceholder.stop();
        }
        this.searchBar.placeholder = '';
        if (this.productsList.childElementCount || this.brandsList.childElementCount) {
            this.show();
        }
        if (screen.width <= 768) {
            scrollToElement(this.searchBar, -12);
        }
        this.onkeyup();
    }
    onblur(event) {
        if (this.searchBar.value.trim() === '') {
            this.searchBar.placeholder = 'Поиск';
        }
        const target = event.relatedTarget;
        if (!target || target && this.searchResults.contains(target)) {
            return;
        }
        this.timeoutId = window.setTimeout(this.hide.bind(this), 200);
    }
    onkeyup(event) {
        const txt = this.searchBar.value.trim().toLowerCase();
        if (event && !event.defaultPrevented && txt !== this.lastValue) {
            this.keyHandler.deselect();
        }
        this.lastValue = txt;
        if (txt.length < 2) {
            this.searchResults.querySelectorAll('.search-result__item').forEach(el => this.shadowCache.appendChild(el));
            this.hide();
            return;
        }
        if (this.cache[txt] === 'requested') {
            return;
        }
        if (this.cache[txt] !== undefined) {
            // @ts-ignore see above condition
            this.updateFromList(this.cache[txt]);
            return;
        }
        if (this.notFound && txt.startsWith(this.notFound)) {
            return;
        }
        this.makeRequest(txt);
    }
    onsubmit(event) {
        if (this.notFound) {
            event?.preventDefault();
            return false;
        }
        if (this.productsBlock.querySelectorAll('.search-result__item').length !== 1) {
            return;
        }
        const link = this.productsBlock.querySelector('a');
        if (link) {
            event?.preventDefault();
            link.click();
        }
    }
    onselect(item) {
        if (item) {
            item.querySelector('a')?.click();
        }
        else {
            this.onsubmit();
        }
    }
    async makeRequest(query) {
        this.cache[query] = 'requested';
        this.showLoader();
        if (this.abortController) {
            this.abortController.abort('cancel');
        }
        this.abortController = new AbortController();
        const data = new FormData();
        data.append('query', query);
        const html = await fetch(URL, {
            method: 'POST',
            credentials: 'same-origin',
            cache: 'no-cache',
            body: data,
            signal: this.abortController.signal,
        })
            .then((response) => {
            this.hideLoader();
            return response.text();
        })
            .catch((reason) => {
            if (reason !== 'cancel') {
                this.hideLoader();
            }
            delete this.cache[query];
        });
        if (!html) {
            return;
        }
        if (!this.updateHtml(query, html)) {
            this.notFound = query;
        }
        else {
            this.notFound = '';
        }
    }
    updateHtml(query, html) {
        const list = this.collectElements(html);
        this.cache[query] = list;
        const result = this.updateFromList(list);
        EventBus.emit('products:load');
        return result;
    }
    updateFromList(list) {
        this.hideNotIncludedItems(list);
        let lastElement;
        let lastBrandElement;
        list.forEach(id => {
            const isBrand = this.suggestionItems[id].classList.contains('search-result__item--brand');
            if (isBrand) {
                this.brandsList.insertBefore(this.suggestionItems[id], lastBrandElement?.nextSibling);
                lastBrandElement = this.suggestionItems[id];
            }
            else {
                this.productsList.insertBefore(this.suggestionItems[id], lastElement?.nextSibling);
                lastElement = this.suggestionItems[id];
            }
        });
        this.toggleBlocks(this.brandsList.childElementCount > 0, this.productsList.childElementCount > 0);
        return !(list.length === 1 && list.includes(0));
    }
    collectElements(html) {
        let list = [];
        const div = document.createElement('div');
        div.innerHTML = html;
        div.querySelectorAll('.search-result__item').forEach((el) => {
            const id = el.dataset.id;
            if (!id) {
                return;
            }
            list.push(parseInt(id));
            if (this.suggestionItems[id] !== undefined) {
                return;
            }
            this.suggestionItems[id] = el;
        });
        return list;
    }
    hideNotIncludedItems(list) {
        this.searchResults.querySelectorAll('.search-result__item').forEach(el => {
            const elementId = el.dataset.id;
            if (!elementId || list.includes(parseInt(elementId))) {
                return;
            }
            this.shadowCache.appendChild(el);
        });
    }
    showLoader() {
        this.icon.classList.add('icon--loading');
    }
    hideLoader() {
        this.icon.classList.remove('icon--loading');
    }
}
export default function initSearchBar() {
    new SearchBar(document.getElementById('main-search'), document.getElementById('search-result'), document.getElementById('search-result-not-found'), document.getElementById('search-result-brands'), document.getElementById('search-result-products'), document.querySelector('.top-search__icon .icon'));
}
